import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useBranding } from "@/lib/branding";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Users, Building2, Clock, Calendar, Plane, BookOpen, Megaphone,
  ListChecks, FileText, MessageCircle, Truck, FlaskConical, ShieldAlert, Palette,
  BarChart3, Menu, LogOut, Sun, Moon, Globe, MonitorSmartphone, User as UserIcon, KeyRound,
  ClipboardList, CalendarClock, ArrowLeftRight
} from "lucide-react";

const NAV_GROUPS = (isAdmin, isManager) => [
  {
    label: "Übersicht",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, key: "dashboard" },
      { to: "/timeclock", icon: Clock, key: "timeclock" },
      { to: "/schedules", icon: Calendar, key: "schedules" },
      { to: "/shift-exchange", icon: ArrowLeftRight, key: "shift_exchange" },
      ...(isManager ? [{ to: "/personnel", icon: CalendarClock, key: "personnel" }] : []),
      { to: "/absences", icon: Plane, key: "absences" },
    ],
  },
  {
    label: "Team",
    items: [
      { to: "/handover", icon: BookOpen, key: "handover" },
      { to: "/announcements", icon: Megaphone, key: "announcements" },
      { to: "/surveys", icon: ClipboardList, key: "surveys" },
      { to: "/tasks", icon: ListChecks, key: "tasks" },
      { to: "/chat", icon: MessageCircle, key: "chat" },
    ],
  },
  {
    label: "Apotheke",
    items: [
      { to: "/delivery", icon: Truck, key: "delivery" },
      { to: "/recipes", icon: FlaskConical, key: "recipes" },
      ...(isManager ? [{ to: "/qm", icon: FileText, key: "qm" }] : []),
    ],
  },
  ...(isAdmin
    ? [
        {
          label: "Verwaltung",
          items: [
            { to: "/users", icon: Users, key: "users" },
            { to: "/locations", icon: Building2, key: "locations" },
            { to: "/requests", icon: Plane, key: "requests" },
            { to: "/admin-timeclock", icon: Clock, key: "admin_timeclock" },
            { to: "/reports", icon: BarChart3, key: "reports" },
            { to: "/audit", icon: ShieldAlert, key: "audit" },
            { to: "/branding", icon: Palette, key: "branding" },
          ],
        },
      ]
    : []),
];

const SidebarContent = ({ onNavigate }) => {
  const { t, lang } = useI18n();
  const { user, isAdmin, isManager } = useAuth();
  const { branding } = useBranding();
  const groups = NAV_GROUPS(isAdmin, isManager);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-border/60">
        {branding?.logo_b64 ? (
          <img src={branding.logo_b64} alt="logo" className="h-9 w-9 rounded-md object-contain" />
        ) : (
          <div className="h-9 w-9 rounded-md bg-blue-700 text-white grid place-items-center font-bold">Rx</div>
        )}
        <div>
          <div className="font-heading font-extrabold tracking-tight text-slate-900 dark:text-slate-100 leading-none">
            {branding?.system_name || "Apotheken Portal"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">
            {user?.position || "Employee"}
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-4">
        {groups.map((g) => (
          <div key={g.label}>
            <div className="overline px-3 mb-2">{g.label}</div>
            <div className="space-y-1">
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.key}`}
                  onClick={onNavigate}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{t(item.key)}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-border/60 p-3 space-y-1">
        <NavLink to="/kiosk" data-testid="nav-kiosk" className="sidebar-link">
          <MonitorSmartphone className="w-4 h-4" />
          <span>{t("kiosk")}</span>
        </NavLink>
      </div>
    </div>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme, branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [countdown, setCountdown] = useState(60);

  // Monitor user inactivity (30 minutes logout for normal roles, excluded for kiosk/admins)
  useEffect(() => {
    if (!user) return;
    
    const isKioskOrAdmin = ["kiosk", "admin", "super_admin"].includes(user.role);
    if (isKioskOrAdmin) return;

    let activityTimer = null;

    const resetActivityTimer = () => {
      if (activityTimer) clearTimeout(activityTimer);
      
      // 29 minutes of inactivity -> trigger 60s countdown warning banner
      if (!showBanner) {
        activityTimer = setTimeout(() => {
          setShowBanner(true);
          setCountdown(60);
        }, 1740 * 1000);
      }
    };

    const events = ["mousemove", "mousedown", "keypress", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetActivityTimer));

    resetActivityTimer();

    return () => {
      if (activityTimer) clearTimeout(activityTimer);
      events.forEach((e) => window.removeEventListener(e, resetActivityTimer));
    };
  }, [user, showBanner]);

  // Handle countdown interval when warning banner is showing
  useEffect(() => {
    if (!showBanner) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showBanner, logout]);

  const keepActive = async () => {
    try {
      // API call to refresh backend PHP session
      await api.get("/auth/me");
      setShowBanner(false);
    } catch (err) {
      logout();
    }
  };

  const title = React.useMemo(() => {
    const path = location.pathname.replace("/", "").split("/")[0];
    return t(path || "dashboard");
  }, [location.pathname, t, lang]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950">
      {showBanner && (
        <div className="sticky top-0 z-50 bg-gradient-to-r from-amber-500 via-orange-600 to-rose-600 text-white px-4 py-2.5 flex items-center justify-between shadow-lg text-sm font-medium animate-pulse">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <span>30 Min. Inaktivität erkannt. Automatische Abmeldung in <strong>{countdown}s</strong>.</span>
          </div>
          <Button
            data-testid="keep-active-btn"
            variant="outline"
            size="sm"
            className="bg-white text-slate-900 border-none hover:bg-slate-100 font-bold h-7 py-0 px-3 rounded-full"
            onClick={keepActive}
          >
            Ich bin aktiv
          </Button>
        </div>
      )}
      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-72 flex-col bg-white dark:bg-slate-900 border-r border-border/60">
          <SidebarContent />
        </aside>

        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-border/60">
            <div className="flex items-center justify-between px-4 lg:px-6 py-3">
              <div className="flex items-center gap-3">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" className="lg:hidden">
                      <Menu className="w-5 h-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="p-0 w-72">
                    <SidebarContent onNavigate={() => setMobileOpen(false)} />
                  </SheetContent>
                </Sheet>
                <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight capitalize">
                  {title}
                </h1>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="btn-lang-toggle"
                  variant="ghost"
                  size="sm"
                  onClick={() => setLang(lang === "de" ? "en" : "de")}
                  className="gap-2"
                >
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs uppercase font-bold">{lang}</span>
                </Button>
                <Button
                  data-testid="btn-theme-toggle"
                  variant="ghost"
                  size="icon"
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                >
                  {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid="user-menu-btn" variant="ghost" className="gap-2 px-2">
                      <Avatar className="w-8 h-8">
                        {user?.avatar_b64 && <AvatarImage src={user.avatar_b64} />}
                        <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">
                          {(user?.first_name?.[0] || "?") + (user?.last_name?.[0] || "")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="hidden sm:block text-left">
                        <div className="text-sm font-medium leading-tight">{user?.first_name} {user?.last_name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{user?.role?.replace("_", " ")}</div>
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>{t("welcome")}, {user?.first_name}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}>
                      <UserIcon className="w-4 h-4 mr-2" /> {t("profile")}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-change-password" onClick={() => navigate("/profile?tab=password")}>
                      <KeyRound className="w-4 h-4 mr-2" /> Passwort
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                      <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="flex-1 p-4 lg:p-6">{children}</main>
          <Footer />
        </div>
      </div>
    </div>
  );
}
